import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, Eraser } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";

const FIELDS = [
  ["last_name", "police.lastName", "text"],
  ["first_names", "police.firstNames", "text"],
  ["birth_date", "police.birthDate", "date"],
  ["birth_place", "police.birthPlace", "text"],
  ["nationality", "police.nationality", "text"],
  ["home_address", "police.home", "text"],
  ["mobile", "police.mobile", "tel"],
  ["email", "police.email", "email"],
];

const SignaturePad = ({ onChange, label, clearLabel }) => {
  const ref = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    const c = ref.current;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * ratio;
    c.height = c.offsetHeight * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.offsetWidth, c.offsetHeight);
    ctx.strokeStyle = "#1b1a17";
  }, []);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - r.left, y: p.clientY - r.top };
  };
  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = ref.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = ref.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(ref.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = ref.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.offsetWidth, c.offsetHeight);
    onChange("");
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium text-bone">{label}</span>
        <button type="button" onClick={clear} data-testid="police-signature-clear"
          className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-bone/72 hover:text-brass">
          <Eraser size={13} /> {clearLabel}
        </button>
      </div>
      <canvas ref={ref} data-testid="police-signature-pad"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="h-40 w-full cursor-crosshair touch-none border border-white/15 bg-white" />
    </div>
  );
};

export default function PoliceForm() {
  const { token } = useParams();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({});
  const [signature, setSignature] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get(`/police/${token}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, [token]);

  const open = (tr) => {
    const d = (data.drafts || {})[tr.index] || {};
    setCurrent(tr);
    setForm({
      last_name: d.last_name || tr.last_name || "",
      first_names: d.first_names || tr.first_name || "",
      birth_date: d.birth_date || "",
      birth_place: d.birth_place || "",
      nationality: d.nationality || tr.nationality || "",
      home_address: d.home_address || "",
      mobile: d.mobile || "", email: d.email || "", children: d.children || "",
    });
    setSignature("");
  };

  const saveDraft = async () => {
    if (!form.last_name || !form.first_names) return toast.error(t("police.error"));
    setSaving(true);
    try {
      await api.post(`/police/${token}/draft`, { ...form, occupant_index: current.index });
      toast.success(t("police.draftSaved"));
      setCurrent(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
      load();
    } catch {
      toast.error(t("police.error"));
    } finally {
      setSaving(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!signature) return toast.error(t("police.needSignature"));
    setSaving(true);
    try {
      await api.post(`/police/${token}`, { ...form, occupant_index: current.index, signature });
      toast.success(t("police.saved"));
      setCurrent(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
      load();
    } catch {
      toast.error(t("police.error"));
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="mx-auto max-w-3xl px-5 py-14 text-muted-foreground sm:px-8">…</div>;
  if (data.error) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-14 sm:px-8">
        <h1 className="display-xl text-3xl text-bone">{t("police.unknown")}</h1>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-5 py-14 sm:px-8" data-testid="police-page">
      <p className="eyebrow">{t("police.eyebrow")}</p>
      <h1 className="display-xl mt-6 text-3xl leading-tight text-bone sm:text-4xl">{t("police.title")}</h1>
      <p className="mt-5 text-sm leading-relaxed text-muted-foreground">{t("police.intro")}</p>
      <p className="mt-3 text-xs leading-relaxed text-bone/72">
        {t("police.stay", { g: data.gite_name, a: data.start_date, d: data.end_date })}
      </p>

      {!current && (
        <div className="mt-10 space-y-3" data-testid="police-travellers">
          {data.travellers.map((tr) => {
            const done = data.signed.includes(tr.index);
            const prepared = !!(data.drafts || {})[tr.index];
            return (
              <div key={tr.index} className="flex flex-wrap items-center justify-between gap-4 border border-white/10 bg-panel px-5 py-4">
                <div>
                  <p className="text-sm text-bone">{tr.last_name} {tr.first_name}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{tr.nationality}</p>
                  {!done && prepared && (
                    <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-brass"
                      data-testid={`police-prepared-${tr.index}`}>{t("police.prepared")}</p>
                  )}
                </div>
                {done ? (
                  <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-brass" data-testid={`police-done-${tr.index}`}>
                    <Check size={14} /> {t("police.done")}
                  </span>
                ) : (
                  <button onClick={() => open(tr)} data-testid={`police-open-${tr.index}`} className="btn-primary">
                    {prepared ? t("police.continue") : t("police.fill")}
                  </button>
                )}
              </div>
            );
          })}
          {!data.travellers.length && (
            <p className="text-sm text-muted-foreground">{t("police.none")}</p>
          )}
        </div>
      )}

      {current && (
        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="police-form">
          <div className="grid gap-5 sm:grid-cols-2">
            {FIELDS.map(([key, labelKey, type]) => (
              <label key={key} className="block text-sm">
                <span className="mb-2 block font-medium text-bone">{t(labelKey)}</span>
                <input type={type} required={!["mobile", "email"].includes(key)} className="field"
                  data-testid={`police-${key}`} value={form[key] || ""}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
              </label>
            ))}
          </div>
          <label className="block text-sm">
            <span className="mb-2 block font-medium text-bone">{t("police.children")}</span>
            <input className="field" data-testid="police-children" value={form.children || ""}
              onChange={(e) => setForm({ ...form, children: e.target.value })} />
          </label>
          <SignaturePad onChange={setSignature} label={t("police.signature")} clearLabel={t("police.clear")} />
          <p className="text-xs leading-relaxed text-brass" data-testid="police-prepare-hint">{t("police.prepareHint")}</p>
          <p className="text-xs leading-relaxed text-bone/72">{t("police.privacy")}</p>
          <div className="flex flex-wrap gap-3">
            <button type="submit" disabled={saving} className="btn-primary" data-testid="police-submit">
              {saving ? <><Loader2 size={16} className="animate-spin" /> {t("police.saving")}</> : t("police.send")}
            </button>
            <button type="button" onClick={saveDraft} disabled={saving} data-testid="police-save-draft"
              className="border border-brass/60 px-6 text-[11px] uppercase tracking-[0.18em] text-brass hover:bg-brass/10">
              {t("police.prepare")}
            </button>
            <button type="button" onClick={() => setCurrent(null)} data-testid="police-cancel"
              className="border border-white/15 px-6 text-[11px] uppercase tracking-[0.18em] text-bone/72 hover:text-brass">
              {t("police.back")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
